import { Component, Inject, OnInit } from '@angular/core';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DashboardTemplateValues } from 'src/app/modules/shared/entities/dashboard-template-values.class';
import { FormBuilder, Validators } from '@angular/forms';
import { ProjectStatus } from 'src/app/modules/shared/entities/project-status.enum';
import { valueInListValidator } from 'src/app/modules/shared/value-in-list-validator/value-in-list-validator';
import { Months } from 'src/app/modules/shared/entities/months.enum';
import { EmailPattern } from 'core/common-entities/email-pattern.class';
import { ConfidenceToClose, ConfidenceToCloseNames } from 'src/app/modules/shared/entities/confidence-to-close.enum';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { UtilsService } from 'core/utils/utils.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ProjectSaleType } from 'src/app/modules/shared/entities/project-sale-type.class';
import { DashboardService } from '../../../services/dashboard.service';
import { ProjectDetails } from '../../../entities/projectDetails';
import { ProjectNote } from '../../../entities/projectNote';

@Component({
  selector: 'app-edit-project-dialog',
  templateUrl: './edit-project-dialog.component.html',
  styleUrls: ['./edit-project-dialog.component.scss'],
  standalone: false
})
export class EditProjectDialogComponent implements OnInit {
  appStrings: AppLocalization;
  isMobile = false;
  dateFormat = this.appInfoService.dateFormat;
  isEdit = false;
  isView = false;
  loading = false;
  dialogData: any;
  date: string;
  lastUpdated: string;
  currencySign: string;
  notes: ProjectNote[];
  isStatusNoteVisible = false;
  editProjectForm: any;
  projectDetails: ProjectDetails;
  title: string;
  buttons = [
    { label: 'Close', type: 'secondary' },
    { label: 'Submit', type: 'primary' },
  ];

  statuses: { value: number, label: string }[];
  types: { value: number, label: string }[];
  industrySegments: { value: number, label: string }[];
  saleTypes: { value: number, label: string }[];
  months: { value: string, label: string }[];
  years: { value: number, label: number }[];
  accountManagers: { value: string, label: string }[];
  confidenceToCloseNames: { value: string, label: string }[];

  constructor(
    public fb: FormBuilder,
    public dialogRef: MatDialogRef<EditProjectDialogComponent>,
    public dashboardService: DashboardService,
    public utilsService: UtilsService,
    private appInfoService: AppInfoService,
    private deviceService: DeviceDetectorService,
    @Inject(MAT_DIALOG_DATA) data
  ) {
    this.dialogData = data;
  }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.isMobile = this.deviceService.isMobile();
    this.currencySign = this.appInfoService.currencySymbol;
    this.isEdit = !!this.dialogData.projectId;
    this.isView = this.dialogData.isView;
    if (this.isView) {
      this.title = this.appStrings.followUp_projects_viewProject;
      this.buttons = [this.buttons[0]];
    } else {
      this.title = this.isEdit ? this.appStrings.followUp_projects_editProject : this.appStrings.followUp_projects_newProject;
    }
    this.confidenceToCloseNames = this.utilsService.mapKeyValueOptions(ConfidenceToCloseNames);
    this.months = this.utilsService.mapKeyValueOptions(Months);
    this.years = this.isEdit ?
      this.utilsService.getYearsSelect(true) :
      this.utilsService.getYearsSelect(false, new Date().getFullYear(), 3);
    this.dashboardService.getStaticValues((templateValues: DashboardTemplateValues) => {
      this.statuses = this.utilsService.mapNumberKeyValueOptions(templateValues.ProjectStatus);
      this.types = this.utilsService.mapNumberKeyValueOptions(templateValues.ProjectType);
      this.industrySegments = this.utilsService.mapNumberKeyValueOptions(templateValues.IndustrySegment);
      this.saleTypes = this.utilsService.mapNumberKeyValueOptions(templateValues.ProjectSaleType);
    });
    this.dashboardService.getFollowupAccountManagers((list: AccountManager[]) => {
      this.accountManagers = this.utilsService.mapObjectToSelect(list, 'id', 'name');
    });

    if (this.isEdit) {
      this.initProjectDetails();
    } else {
      this.projectDetails = new ProjectDetails();
      this.initForm();
    }
  }

  initForm() {
    const estimatedSalesYear = Number(this.projectDetails.estimatedSalesYear);
    this.editProjectForm = this.fb.group({
      projectId: [this.projectDetails.projectId],
      projectOwnerId: [this.projectDetails.projectOwnerId, [
        Validators.required,
        valueInListValidator(this.accountManagers.map(obj => obj.value))]],
      companyName: [this.projectDetails.companyName, [Validators.required, Validators.maxLength(200)]],
      projectName: [this.projectDetails.projectName, Validators.maxLength(200)],
      industrySegmentId: [this.projectDetails.industrySegmentId, Validators.required],
      projectTypeIds: [this.projectDetails.projectTypeIds, Validators.required],
      projectStatusId: [this.projectDetails.projectStatusId, Validators.required],
      projectStatusNote: ['', Validators.maxLength(3000)],
      projectAndProductDetails: [this.projectDetails.projectAndProductDetails, [Validators.required, Validators.maxLength(200)]],
      estimatedSalesMonth: [this.projectDetails.estimatedSalesMonth, Validators.required],
      estimatedSalesYear: [isNaN(estimatedSalesYear) ? null : estimatedSalesYear, Validators.required],
      contactName: [this.projectDetails.contactName, [Validators.required, Validators.maxLength(100)]],
      contactEmail: [this.projectDetails.contactEmail, [
        Validators.email,
        Validators.pattern(EmailPattern.pattern),
        Validators.maxLength(100)]],
      contactPhoneNumber: [this.projectDetails.contactPhoneNumber, Validators.maxLength(100)],
      saleTypeId: [this.projectDetails.saleTypeId],
      distributorName: [this.projectDetails.distributorName, Validators.maxLength(500)],
      techResourcesNeeded: [this.projectDetails.techResourcesNeeded],
      confidenceToClose: [ConfidenceToClose[this.projectDetails.confidenceToCloseId], Validators.required],
      estimatedSalesValue: [this.projectDetails.estimatedSalesValue, [
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.maxLength(10)]],
      note: ['', Validators.maxLength(3000)]
    });

    if (this.isView) {
      this.disableForm();
    }
  }

  initProjectDetails() {
    this.loading = true;
    this.dashboardService.getProjectInfo(this.dialogData.projectId).subscribe((result: ProjectDetails) => {
      this.date = result.date;
      this.lastUpdated = result.lastUpdated;
      this.projectDetails = result;
      this.notes = result.notes;
      this.initForm();
      this.loading = false;
    });
  }

  disableForm() {
    const form = this.editProjectForm;
    form.disable();
    Object.keys(form.controls).forEach(key => {
      form.get(key)?.clearValidators();
      form.get(key)?.updateValueAndValidity();
    });
  }

  handleStatusChange() {
    const controls = this.editProjectForm.controls;
    const noteValidators = [Validators.required, Validators.maxLength(3000)];
    if (controls.projectStatusId.value === ProjectStatus.Cancelled) {
      this.isStatusNoteVisible = true;
      controls.projectStatusNote.setValidators(noteValidators);
    } else {
      this.isStatusNoteVisible = false;
      controls.projectStatusNote.removeValidators(noteValidators);
    }
    controls.projectStatusNote.updateValueAndValidity();
  }

  isDistributorNameVisible() {
    return this.editProjectForm.controls.saleTypeId.value === ProjectSaleType.Distribution;
  }

  btnCallback(i: number) {
    if (i === 0) {
      this.dialogRef.close();
    } else {
      this.editProjectForm.markAllAsTouched();
      const { value, valid } = this.editProjectForm;
      if (valid) {
        this.loading = true;
        if (this.isEdit) {
          this.dashboardService.saveProjectInfo(value).subscribe(() => {
            this.loading = false;
            this.dialogRef.close({ shouldRefresh: true });
          });
        } else {
          this.dashboardService.createProject(value).subscribe(() => {
            this.loading = false;
            this.dialogRef.close({ shouldRefresh: true });
          });
        }

      }
    }
  }
}
